import React from "react";
import {
  BadgeDollarSignIcon,
  BadgeEuroIcon,
  BadgeIndianRupeeIcon,
  BadgeJapaneseYenIcon,
  BadgePoundSterlingIcon,
  WalletIcon,
  CalendarClockIcon,
  BellRingIcon,
  AlertCircleIcon,
} from "lucide-react";
import { NotificationItem } from "@/actions/notification.actions";
import { Tag } from "antd";

/**
 * Renders currency-aware icon for Customer Payment.
 * Defaults to Indian Rupee (INR) for this tenant, but adapts if another currency is explicitly present.
 */
export const CustomerPaymentIcon: React.FC<{
  item?: Partial<NotificationItem> | null;
  className?: string;
}> = ({ item, className = "h-4 w-4" }) => {
  const text = String(item?.data?.currency || "INR").toUpperCase();

  if (text.includes("USD") || text.includes("$")) {
    return <BadgeDollarSignIcon className={className} />;
  }
  if (text.includes("EUR") || text.includes("€")) {
    return <BadgeEuroIcon className={className} />;
  }
  if (text.includes("GBP") || text.includes("£")) {
    return <BadgePoundSterlingIcon className={className} />;
  }
  if (text.includes("JPY") || text.includes("¥")) {
    return <BadgeJapaneseYenIcon className={className} />;
  }
  // Default to Indian Rupee (INR)
  return <BadgeIndianRupeeIcon className={className} />;
};

/**
 * Returns formatted notification icon container based on notification type and data.
 */
export const getNotificationIcon = (
  type?: NotificationItem["type"],
  item?: Partial<NotificationItem> | null,
  size: "sm" | "md" | "lg" = "md"
) => {
  const sizeClasses = {
    sm: { container: "h-8 w-8 rounded-lg", icon: "h-4 w-4" },
    md: { container: "h-9 w-9 rounded-xl", icon: "h-4.5 w-4.5" },
    lg: { container: "h-10 w-10 rounded-xl", icon: "h-5 w-5" },
  }[size];

  switch (type) {
    case "customer_payment":
      return (
        <span className={`flex ${sizeClasses.container} items-center justify-center bg-gray-50 text-[#131B2A] shrink-0 border border-gray-200`}>
          <CustomerPaymentIcon item={item} className={sizeClasses.icon} />
        </span>
      );
    case "supplier_payment":
      return (
        <span className={`flex ${sizeClasses.container} items-center justify-center bg-gray-50 text-[#131B2A] shrink-0 border border-gray-200`}>
          <WalletIcon className={sizeClasses.icon} />
        </span>
      );
    case "followup":
      return (
        <span className={`flex ${sizeClasses.container} items-center justify-center bg-gray-50 text-[#131B2A] shrink-0 border border-gray-200`}>
          <CalendarClockIcon className={sizeClasses.icon} />
        </span>
      );
    case "reminder":
      return (
        <span className={`flex ${sizeClasses.container} items-center justify-center bg-gray-50 text-[#131B2A] shrink-0 border border-gray-200`}>
          <BellRingIcon className={sizeClasses.icon} />
        </span>
      );
    default:
      return (
        <span className={`flex ${sizeClasses.container} items-center justify-center bg-gray-50 text-gray-600 shrink-0 border border-gray-100`}>
          <AlertCircleIcon className={sizeClasses.icon} />
        </span>
      );
  }
};

/** Labels notification categories using the product's neutral status treatment. */
export const getTypeTag = (type?: NotificationItem["type"]) => {
  switch (type) {
    case "customer_payment":
      return <Tag bordered={false} className="!m-0 !text-xs !font-medium !text-[#131B2A] !bg-gray-50 !border-gray-200">Customer Payment</Tag>;
    case "supplier_payment":
      return <Tag bordered={false} className="!m-0 !text-xs !font-medium !text-[#131B2A] !bg-gray-50 !border-gray-200">Supplier Payment</Tag>;
    case "followup":
      return <Tag bordered={false} className="!m-0 !text-xs !font-medium !text-[#131B2A] !bg-gray-50 !border-gray-200">Follow-up</Tag>;
    case "reminder":
      return <Tag bordered={false} className="!m-0 !text-xs !font-medium !text-[#131B2A] !bg-gray-50 !border-gray-200">Reminder</Tag>;
    default:
      return <Tag bordered={false} className="!m-0 !text-xs !font-medium !text-[#131B2A] !bg-gray-50 !border-gray-200">Notification</Tag>;
  }
};

